import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { fetchAuthSession, type JWT } from "aws-amplify/auth";
import { useAuthenticator } from "@aws-amplify/ui-react";

import BouncingDotsLoader from "../components/Loader/BouncingDotsLoader";
import { useFeatureFlags } from "./FeatureFlags";

export type TokenCacheContextValue = {
  groups: string[];
  idToken: JWT;
  authStatus: "configuring" | "authenticated" | "unauthenticated";
  refreshTokens: (forceRefresh?: boolean) => Promise<void>;
};

export type TokenCacheProviderProps = {
  children?: React.ReactNode;
}

export const TokenCacheContext = createContext<TokenCacheContextValue>(null!);

export const useTokenCache = () : TokenCacheContextValue => useContext(TokenCacheContext);

export const TokenCacheProvider : React.FC<TokenCacheProviderProps> = ({ children }) => {
  const { authStatus } = useAuthenticator(context => [context.authStatus]);
  const [idToken, setIdToken] = useState<JWT | null>(null);
  const [groups, setGroups] = useState<string[] | null>(null);

  const { REQUIRE_GOOD_MFA } = useFeatureFlags();

  const refreshTokens = useCallback(async (forceRefresh : boolean = false) : Promise<void> => {
    let user = await fetchAuthSession({ forceRefresh });
    let newIdToken = user.tokens?.idToken;

    // The good_mfa claim should always be present.
    // If it isn't, we may be in a situation where the system has just been deployed, and newIdToken was cached beforehand.
    // Forcing a refresh should sort that out.
    if (REQUIRE_GOOD_MFA && !forceRefresh && newIdToken && !("good_mfa" in newIdToken.payload)) {
      user = await fetchAuthSession({ forceRefresh: true });
      newIdToken = user.tokens?.idToken;
    }

    setIdToken(newIdToken || null);
    setGroups(newIdToken?.payload["cognito:groups"] as string[] || null);
  }, []);

  useEffect(() => {
    refreshTokens();
  }, []);

  const contextValue = useMemo(
    () => ({ authStatus, idToken: idToken!, groups: groups!, refreshTokens }),
    [authStatus, idToken, groups, refreshTokens]);

  if (idToken === null || groups === null)
    return <BouncingDotsLoader />;

  return (
    <TokenCacheContext.Provider value={contextValue}>
      {children}
    </TokenCacheContext.Provider>
  );
};
